/**
 * DistributionCard — the card BODY, and nothing else.
 *
 * The page still owns its route, its auth, its data fetch and its canonical
 * variant lock. This component is a leaf: it is handed a payload and a state
 * and it paints. It opens no connection, reads no token and knows no table.
 *
 * COLOUR COMES FROM THE BRAND, ALWAYS. Every wedge, every legend swatch and
 * every accent in here resolves to var(--chart-1)..var(--chart-5), which
 * lib/brand-inheritance.js derives per theme from the tenant's locked accent.
 * There is no hex literal in this file and there must never be one: a
 * hardcoded colour is a client seeing another client's brand.
 *
 * THE EIGHT STATES ARE THE POINT. A donut of zeroes drawn when the truth is
 * "the integration was never connected" is a lie the operator will act on.
 * Each state below says a DIFFERENT true thing, and the ones that carry
 * partial truth (partial / stale / degraded / permission-limited) still draw
 * the chart, because hiding real data behind a banner is its own dishonesty.
 */
import * as React from 'react';
import { Pie, PieChart, Label } from 'recharts';
import {
  Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle
} from './ui/card';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
  type ChartConfig
} from './ui/chart';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

/* The five chart slots the brand compiler guarantees. A series beyond the
   fifth reuses the ladder rather than inventing a sixth colour — five is
   what the token contract promises, so five is what this asks for. */
/* Each slot carries the SAME neutral fallback the Tailwind config uses, for
   the same reason: these strings are handed to Recharts as inline fills, so
   they bypass Tailwind entirely and need their own fallback for the window
   before the compiler has painted. Neutral, never a guessed brand colour. */
const SLOTS = [
  'hsl(var(--sc-chart-1, 0 0% 30%))',
  'hsl(var(--sc-chart-2, 0 0% 45%))',
  'hsl(var(--sc-chart-3, 0 0% 58%))',
  'hsl(var(--sc-chart-4, 0 0% 70%))',
  'hsl(var(--sc-chart-5, 0 0% 80%))'
];

export type DistributionSeries = { key: string; label: string; value: number };

export type DistributionState =
  | 'loading'                  /* the fetch has not landed yet */
  | 'ok'                       /* everything below is true and complete */
  | 'empty'                    /* the read landed and observed zero records */
  | 'not-connected'            /* the upstream integration was never wired */
  | 'partial'                  /* some sources answered, some did not */
  | 'stale'                    /* real data, but older than its cadence */
  | 'permission-limited'       /* the operator may see less than exists */
  | 'degraded'                 /* the read succeeded through a fallback */
  | 'error';                   /* the read was refused or failed */

export type DistributionPayload = {
  title: string;
  description?: string | null;
  /* Total shown in the middle of the donut, and the word under it. Never
     derived from the visible wedges when the truth is a larger set — the
     caller passes the real total. */
  total?: number | null;
  totalCaption?: string | null;
  series?: DistributionSeries[];
  footer?: { label: string; href?: string | null; note?: string | null } | null;
  /* State copy. Every one of these is written by the caller from the
     section's own seeded copy, never invented here. */
  stateHeadline?: string | null;
  stateBody?: string | null;
  /* stale: when the data was actually observed. partial/permission-limited:
     what is missing and why. */
  asOf?: string | null;
  missingNote?: string | null;
  /* error only: whether a retry is offered at all. */
  canRetry?: boolean;
  /* 'host': the card is mounted INSIDE a surface that already draws card
     chrome (the canonical page tile), so this Card drops its own border,
     shadow and ground rather than stacking a second frame inside the first.
     Absent or 'own': the Card draws itself, for a bare mount point. */
  chrome?: 'own' | 'host';
};

function pct(n: number, total: number) {
  if (!total) return '0%';
  const v = (n / total) * 100;
  return (v >= 10 || v === 0 ? Math.round(v) : Math.round(v * 10) / 10) + '%';
}

/* The chart half. Shared by every state that has real data to draw. */
function Donut({
  series, total, totalCaption
}: { series: DistributionSeries[]; total: number; totalCaption: string }) {
  const data = React.useMemo(
    () => series.map((s, i) => ({ ...s, fill: SLOTS[i % SLOTS.length] })),
    [series]
  );
  const config = React.useMemo<ChartConfig>(() => {
    const c: ChartConfig = {};
    series.forEach((s, i) => { c[s.key] = { label: s.label, color: SLOTS[i % SLOTS.length] }; });
    return c;
  }, [series]);
  const shown = series.reduce((a, s) => a + s.value, 0);

  return (
    <ChartContainer config={config} className="mx-auto aspect-square max-h-[260px] w-full">
      <PieChart>
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              hideLabel
              nameKey="key"
              /* Percentage AND absolute count, both, always. A percentage
                 with no denominator cannot be acted on, and a count with no
                 share cannot be compared. */
              formatter={(value: any, _name: any, item: any) => (
                <div className="flex w-full items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: item?.payload?.fill }}
                  />
                  <span className="text-muted-foreground">{item?.payload?.label}</span>
                  <span className="ml-auto font-mono font-medium tabular-nums text-foreground">
                    {Number(value).toLocaleString()}
                  </span>
                  <span className="font-mono tabular-nums text-muted-foreground">
                    {pct(Number(value), shown)}
                  </span>
                </div>
              )}
            />
          }
        />
        <Pie data={data} dataKey="value" nameKey="key" innerRadius={62} strokeWidth={3} paddingAngle={1}>
          <Label
            content={({ viewBox }) => {
              if (!viewBox || !('cx' in viewBox)) return null;
              return (
                <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                  <tspan
                    x={viewBox.cx}
                    y={viewBox.cy}
                    className="fill-foreground text-2xl font-semibold tabular-nums"
                  >
                    {total.toLocaleString()}
                  </tspan>
                  <tspan
                    x={viewBox.cx}
                    y={(viewBox.cy || 0) + 20}
                    className="fill-muted-foreground text-xs"
                  >
                    {totalCaption}
                  </tspan>
                </text>
              );
            }}
          />
        </Pie>
        <ChartLegend content={<ChartLegendContent nameKey="key" className="flex-wrap gap-x-4 gap-y-1" />} />
      </PieChart>
    </ChartContainer>
  );
}

/* The per-series read-out under the chart: share AND count, per the brief.
   The chart shows proportion; this is what an operator copies into a ticket. */
function SeriesReadout({ series }: { series: DistributionSeries[] }) {
  const shown = series.reduce((a, s) => a + s.value, 0);
  return (
    <ul className="mt-1 grid gap-1.5 text-sm">
      {series.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
            style={{ backgroundColor: SLOTS[i % SLOTS.length] }}
            aria-hidden="true"
          />
          <span className="truncate text-muted-foreground">{s.label}</span>
          <span className="ml-auto font-mono tabular-nums text-foreground">
            {s.value.toLocaleString()}
          </span>
          <span className="w-12 text-right font-mono tabular-nums text-muted-foreground">
            {pct(s.value, shown)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* A shape-matched skeleton: a ring the size of the real ring, a legend row
   the width of the real legend, and as many read-out rows as the caller
   expects. Never a spinner (a spinner says "wait", not "what is coming"),
   and never a zero (a zero is a claim about the data). */
function LoadingShape({ rows = 4 }: { rows?: number }) {
  return (
    <div className="grid gap-4" aria-busy="true" aria-live="polite">
      <div className="mx-auto flex aspect-square max-h-[260px] w-full items-center justify-center">
        <Skeleton className="h-[190px] w-[190px] rounded-full" />
      </div>
      <div className="flex justify-center gap-4">
        {Array.from({ length: Math.min(rows, 4) }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-16" />
        ))}
      </div>
      <div className="grid gap-1.5">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-2.5 w-2.5 rounded-[2px]" />
            <Skeleton className="h-3 w-28" />
            <Skeleton className="ml-auto h-3 w-10" />
            <Skeleton className="h-3 w-8" />
          </div>
        ))}
      </div>
      <span className="sr-only">Loading distribution</span>
    </div>
  );
}

/* An honest empty: an outline where the ring would be, and the section's own
   seeded copy. The ring is drawn as an EMPTY outline rather than a full
   circle, so it never reads as one series at 100%. */
function HonestEmpty({ headline, body }: { headline: string; body?: string | null }) {
  return (
    <div className="grid place-items-center gap-3 py-8 text-center">
      <div className="h-[132px] w-[132px] rounded-full border-2 border-dashed border-border" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">{headline}</p>
      {body ? <p className="max-w-[42ch] text-sm text-muted-foreground">{body}</p> : null}
    </div>
  );
}

/* The one-line qualifier above a chart that IS drawn but is not the whole
   truth. It never replaces the chart — it qualifies it. */
function Qualifier({
  tone, children
}: { tone: 'warn' | 'info'; children: React.ReactNode }) {
  return (
    <div
      className={
        'mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ' +
        (tone === 'warn'
          ? 'border-destructive/40 bg-destructive/10 text-foreground'
          : 'border-border bg-muted text-muted-foreground')
      }
    >
      <span>{children}</span>
    </div>
  );
}

export function DistributionCard({
  state, payload, onRetry
}: { state: DistributionState; payload: DistributionPayload; onRetry?: () => void }) {
  const series = (payload.series || []).filter((s) => s && isFinite(s.value));
  const shown = series.reduce((a, s) => a + s.value, 0);
  /* The centred total is the caller's total when it gave one — which may be
     LARGER than the wedges sum (a top-N chart of a longer tail). Falling back
     to the wedge sum silently would misreport that tail as absent. */
  const total = payload.total != null && isFinite(payload.total) ? payload.total : shown;
  const caption = payload.totalCaption || 'total';

  /* States that draw the chart, because they have real data. */
  const DRAWS = new Set<DistributionState>(['ok', 'partial', 'stale', 'permission-limited', 'degraded']);
  const draws = DRAWS.has(state) && series.length > 0;

  const badge: Record<string, { text: string; variant: 'secondary' | 'destructive' | 'outline' }> = {
    partial: { text: 'Partial', variant: 'secondary' },
    stale: { text: 'Stale', variant: 'secondary' },
    'permission-limited': { text: 'Limited view', variant: 'outline' },
    degraded: { text: 'Degraded', variant: 'secondary' },
    'not-connected': { text: 'Not connected', variant: 'outline' },
    error: { text: 'Unreadable', variant: 'destructive' }
  };
  const b = badge[state];

  return (
    <Card
      className={
        'flex h-full flex-col' +
        (payload.chrome === 'host' ? ' border-0 bg-transparent shadow-none' : '')
      }
      data-ora-card-state={state}
    >
      <CardHeader className={payload.chrome === 'host' ? 'items-start p-0 pb-3' : 'items-start pb-3'}>
        <div className="flex w-full items-start justify-between gap-3">
          <div className="grid gap-1">
            <CardTitle className="text-base">{payload.title}</CardTitle>
            {payload.description ? <CardDescription>{payload.description}</CardDescription> : null}
          </div>
          {b ? <Badge variant={b.variant}>{b.text}</Badge> : null}
        </div>
      </CardHeader>

      <CardContent className={payload.chrome === 'host' ? 'flex-1 p-0 pb-3' : 'flex-1 pb-3'}>
        {state === 'loading' ? <LoadingShape rows={Math.max(series.length || 0, 4)} /> : null}

        {state === 'empty' ? (
          <HonestEmpty
            headline={payload.stateHeadline || 'Nothing here yet'}
            body={payload.stateBody}
          />
        ) : null}

        {state === 'not-connected' ? (
          <HonestEmpty
            headline={payload.stateHeadline || 'This integration is not connected'}
            /* NOT a zero. Nothing has ever been observed, so there is no
               number to draw — saying "0" here would report an absent feed
               as an empty one. */
            body={payload.stateBody || 'Nothing has been read from this source yet, so there is no distribution to show.'}
          />
        ) : null}

        {state === 'error' ? (
          <div className="grid place-items-center gap-3 py-8 text-center">
            <div
              className="h-[132px] w-[132px] rounded-full border-2 border-dashed border-destructive/50"
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-foreground">
              {payload.stateHeadline || 'Cannot read this right now'}
            </p>
            <p className="max-w-[42ch] text-sm text-muted-foreground">
              {payload.stateBody || 'The read was refused or failed.'}
            </p>
            {payload.canRetry !== false && onRetry ? (
              <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>
            ) : null}
          </div>
        ) : null}

        {draws ? (
          <>
            {state === 'partial' ? (
              <Qualifier tone="warn">
                {payload.missingNote || 'Some sources did not answer. The shares below are of what did.'}
              </Qualifier>
            ) : null}
            {state === 'stale' ? (
              <Qualifier tone="warn">
                {payload.asOf
                  ? `Last observed ${payload.asOf}. Newer records may exist.`
                  : 'Older than its expected cadence. Newer records may exist.'}
              </Qualifier>
            ) : null}
            {state === 'permission-limited' ? (
              <Qualifier tone="info">
                {payload.missingNote || 'You are seeing the rows your role can read. More may exist.'}
              </Qualifier>
            ) : null}
            {state === 'degraded' ? (
              <Qualifier tone="info">
                {payload.missingNote || 'Read through a fallback. Figures are real but may lag the source.'}
              </Qualifier>
            ) : null}
            <Donut series={series} total={total} totalCaption={caption} />
            <SeriesReadout series={series} />
            {total > shown ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {(total - shown).toLocaleString()} more outside the top {series.length}.
              </p>
            ) : null}
          </>
        ) : null}

        {/* A state that DRAWS but arrived with no series is not a donut of
            zeroes — it is an empty with its qualifier still stated. */}
        {DRAWS.has(state) && series.length === 0 ? (
          <HonestEmpty
            headline={payload.stateHeadline || 'No breakdown to show'}
            body={payload.stateBody || payload.missingNote}
          />
        ) : null}
      </CardContent>

      {payload.footer ? (
        <CardFooter
          className={
            'flex-col items-start gap-1 border-t pt-3 text-sm' +
            (payload.chrome === 'host' ? ' px-0 pb-0' : '')
          }
        >
          {payload.footer.href ? (
            <a
              className="font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
              href={payload.footer.href}
            >
              {payload.footer.label} →
            </a>
          ) : (
            <span className="font-medium text-foreground">{payload.footer.label}</span>
          )}
          {payload.footer.note ? (
            <span className="text-xs text-muted-foreground">{payload.footer.note}</span>
          ) : null}
        </CardFooter>
      ) : null}
    </Card>
  );
}
